/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../../../../components/Modal/Modal';
import { GhostButton, PrimaryButton } from '../../../../styles/ui/Button';
import { Textarea } from '../../../../styles/ui/Form';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import { FooterNote, ModalIntro } from './modalKit';
import { SETTLEMENT_MIN } from '../constants';
import { productLabel, routeLabel } from '../utils/format';

// =============================================================================
// TRATATIVA da divergência (POST /transfer-shipments/:id/settle-divergence).
//
// Ato de gestor, auditado e SEM efeito de estoque: a perda já está refletida
// (saiu o expedido, entrou o conferido). A tratativa fecha a CONTAGEM e
// documenta o desfecho — e pode destravar o CLOSED da ordem.
//
// Um registro por item divergente ainda não tratado; item já tratado sai da
// lista (o backend responde 422 se vier de novo). Reposição, quando houver, é
// ordem nova — esta tela não reabre a antiga.
// =============================================================================

const ItemCard = styled.div`
   border: 1px solid rgba(0, 0, 0, 0.12);
   border-radius: 10px;
   padding: 12px 14px;
   margin-bottom: 12px;
   background: #fff;
`;

const ItemHead = styled.div`
   display: flex;
   flex-wrap: wrap;
   align-items: baseline;
   justify-content: space-between;
   gap: 8px;
   margin-bottom: 8px;
`;

const ItemTitle = styled.strong`
   font-size: 13.5px;
`;

const ItemNumbers = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: #8a5a00;
`;

const ReasonQuote = styled.p`
   margin: 0 0 10px;
   font-size: 12.5px;
   line-height: 1.5;
   color: rgba(0, 0, 0, 0.65);
   font-style: italic;
`;

const SettlementLabel = styled.label`
   display: block;
   margin-bottom: 4px;
   font-size: 12px;
   font-weight: 700;
   color: rgba(0, 0, 0, 0.7);
`;

const InlineError = styled.small`
   display: block;
   margin-top: 6px;
   font-size: 12px;
   font-weight: 600;
   color: ${({ theme }) => theme.colors.danger};
`;

// Item divergente ainda sem tratativa registrada.
function isPending(item) {
   return Number(item.divergent_qtd) > 0 && !item.divergence_settled_at;
}

export default function SettleDivergenceModal({
   isOpen,
   shipment,
   isSubmitting = false,
   onCancel,
   onConfirm,
}) {
   const [settlements, setSettlements] = useState({});
   const [touched, setTouched] = useState(false);

   const pendingItems = useMemo(
      () => (shipment?.items ?? []).filter(isPending),
      [shipment]
   );

   useEffect(() => {
      if (!isOpen) return;
      setSettlements({});
      setTouched(false);
   }, [isOpen, shipment]);

   // O contrato aceita tratativa parcial (um registro por item), então basta um
   // preenchido para confirmar — os vazios ficam de fora do payload.
   const filledItems = pendingItems
      .map((item) => ({
         prodId: item.prod_id,
         settlement: (settlements[item.prod_id] ?? '').trim(),
      }))
      .filter((item) => item.settlement.length > 0);

   const hasTooShort = filledItems.some(
      (item) => item.settlement.length < SETTLEMENT_MIN
   );

   const canConfirm = filledItems.length > 0 && !hasTooShort && !isSubmitting;

   const handleConfirm = () => {
      setTouched(true);
      if (!canConfirm) return;
      onConfirm({ items: filledItems });
   };

   return (
      <Modal
         isOpen={isOpen}
         title="Registrar tratativa da divergência"
         subtitle={
            shipment
               ? `Remessa #${shipment.id} · ${routeLabel(shipment)}`
               : null
         }
         onClose={isSubmitting ? undefined : onCancel}
         maxWidth="720px"
         footer={
            <>
               {pendingItems.length > 0 && (
                  <FooterNote>
                     {filledItems.length} de {pendingItems.length} desfecho(s)
                     preenchido(s) — tratativa parcial é permitida.
                  </FooterNote>
               )}
               <GhostButton
                  type="button"
                  onClick={onCancel}
                  disabled={isSubmitting}
               >
                  Cancelar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={!canConfirm}
               >
                  {isSubmitting ? 'Registrando…' : 'Registrar tratativa'}
               </PrimaryButton>
            </>
         }
      >
         {pendingItems.length === 0 ? (
            <EmptyState
               title="Nenhuma divergência pendente"
               description="Todos os itens divergentes desta remessa já têm tratativa registrada."
            />
         ) : (
            <>
               <ModalIntro $tone="neutral">
                  A tratativa <strong>não altera o estoque</strong> — a perda já
                  foi refletida na conferência. Ela fecha a contagem e registra o
                  desfecho para auditoria. Se houver reposição, ela é uma nova
                  transferência.
               </ModalIntro>

               {pendingItems.map((item) => {
                  const value = settlements[item.prod_id] ?? '';
                  const isTooShort =
                     value.trim().length > 0 &&
                     value.trim().length < SETTLEMENT_MIN;
                  return (
                     <ItemCard key={item.prod_id}>
                        <ItemHead>
                           <ItemTitle>{productLabel(item)}</ItemTitle>
                           <ItemNumbers>
                              {item.received_qtd} de {item.shipped_qtd} contados
                              · falta {item.divergent_qtd}
                           </ItemNumbers>
                        </ItemHead>

                        {item.divergence_reason && (
                           <ReasonQuote>
                              Motivo informado na conferência: “
                              {item.divergence_reason}”
                           </ReasonQuote>
                        )}

                        <SettlementLabel htmlFor={`settlement-${item.prod_id}`}>
                           Desfecho
                        </SettlementLabel>
                        <Textarea
                           id={`settlement-${item.prod_id}`}
                           rows={2}
                           value={value}
                           disabled={isSubmitting}
                           aria-invalid={(touched && isTooShort) || undefined}
                           $invalid={touched && isTooShort}
                           placeholder="Ex.: Perda em transporte — desconto do responsável"
                           onChange={(event) =>
                              setSettlements((prev) => ({
                                 ...prev,
                                 [item.prod_id]: event.target.value,
                              }))
                           }
                        />
                        {isTooShort && (
                           <InlineError>
                              Descreva o desfecho com pelo menos{' '}
                              {SETTLEMENT_MIN} caracteres.
                           </InlineError>
                        )}
                     </ItemCard>
                  );
               })}

               {touched && filledItems.length === 0 && (
                  <InlineError>
                     Registre o desfecho de pelo menos um item.
                  </InlineError>
               )}
            </>
         )}
      </Modal>
   );
}

SettleDivergenceModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   shipment: PropTypes.object,
   isSubmitting: PropTypes.bool,
   onCancel: PropTypes.func.isRequired,
   onConfirm: PropTypes.func.isRequired,
};
